import posthog from "posthog-js";

/**
 * Typed event helpers for PostHog.
 *
 * Why a wrapper:
 * - Single source of truth for event names + payload shapes (typo-proof)
 * - Soft-fails if PostHog isn't ready yet (prevents first-paint crashes)
 * - Easy to grep usages
 *
 * Convention: snake_case event names. Use namespaced prefixes
 * (landing_, demo_, explore_, public_scenario_, scenario_create_).
 */

type Difficulty = "easy" | "medium" | "hard";

export type AnalyticsEvents = {
  // ----- Landing page -----
  landing_viewed: { logged_in: boolean };
  landing_cta_clicked: {
    location: "hero" | "footer" | "demo_result";
    cta: string;
  };
  landing_link_clicked: {
    destination: "explore" | "leaderboard" | "sign_in" | "discord";
  };
  landing_trending_clicked: {
    scenario_id: number;
    slug: string | null;
    position: number;
  };

  // ----- Demo -----
  demo_test_shuffled: { test: string };
  demo_submitted: { test: string; response_length: number };
  demo_received: {
    score: number;
    rating: "weak" | "mid" | "solid" | "savage";
  };
  demo_cta_clicked: { rating: "weak" | "mid" | "solid" | "savage" };

  // ----- FAQ -----
  faq_expanded: { question_id: string };

  // ----- Marketplace -----
  explore_viewed: {
    sort: string;
    difficulty: string;
    result_count: number;
  };
  explore_filter_changed: {
    filter_type: "sort" | "difficulty";
    value: string;
  };
  explore_scenario_clicked: {
    scenario_id: number;
    slug: string | null;
    position: number;
  };
  explore_create_clicked: { from: "header" | "footer" | "empty_state" };

  // ----- Public scenario page -----
  public_scenario_viewed: {
    scenario_id: number;
    slug: string;
    creator_handle: string | null;
    play_count: number;
    logged_in: boolean;
  };
  public_scenario_play_clicked: {
    scenario_id: number;
    slug: string;
    logged_in: boolean;
  };
  public_scenario_sign_in_clicked: { scenario_id: number; slug: string };

  // ----- Scenario creation wizard -----
  scenario_create_step_advanced: { from_step: number; to_step: number };
  scenario_create_public_toggled: { is_public: boolean };
  scenario_created: {
    scenario_id: number;
    is_public: boolean;
    difficulty: Difficulty;
    is_update: boolean;
  };

  // ----- AI / LLM traffic attribution -----
  // Fired by entry.client.tsx when document.referrer matches a known AI
  // chat / search host. Also registered as a super-property on all events.
  ai_referrer_landed: {
    ai_referrer: string;
    referer: string;
    path: string;
  };
};

export const track = <K extends keyof AnalyticsEvents>(
  event: K,
  properties: AnalyticsEvents[K]
): void => {
  try {
    posthog.capture(event, properties as Record<string, unknown>);
  } catch {
    // PostHog can fail before init; never let analytics break the UI.
  }
};

export type SignupSource =
  | "landing_hero"
  | "landing_footer"
  | "landing_demo"
  | "public_scenario"
  | "explore_create"
  | "paywall"
  | "create_account_modal"
  | "direct";

/**
 * Tags the current (anonymous) user with where they entered the signup
 * funnel. Once they sign up, this property persists on the Person in
 * PostHog, so you can segment LTV / retention by acquisition source.
 *
 * Call this at the moment a user clicks something that routes them to
 * /onboard. The property survives identify() at signup.
 */
export const setSignupSource = (
  source: SignupSource,
  context: Record<string, unknown> = {}
): void => {
  try {
    posthog.setPersonProperties(undefined, {
      signup_source: source,
      signup_source_at: new Date().toISOString(),
      ...context,
    });
  } catch {
    // Same soft-fail as track().
  }
};
